import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import axios from 'axios';
import { EXCHNAGE_UR_PAYMENTSL } from '../../../Url/Url';
import { RedBackgroundButton } from '../../Global/GlobalButton';
import './StripePayment.scss';
import { useNavigate } from 'react-router-dom';

const StripePayment = ({ 
  companyName,
  gstNumber,
  vatNumber,        // ✅ ADD VAT NUMBER
  finalAmount,
  eventId,
  onPaymentSuccess,
  setLoading,
  currencyType,
  currencySymbol,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [showCardForm, setShowCardForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    addressLine1: '',
  });
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStripePay = async () => {
    if (!showCardForm) {
      setShowCardForm(true);
      return;
    }

    if (!billingDetails.name || !billingDetails.addressLine1) {
      toast.error('Please fill all required billing details');
      return;
    }

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setLoading(true);

    try {
      const totalPrice = finalAmount || 0;

      // ✅ Determine which tax number to send
      const taxNumber = currencyType === "INR" ? gstNumber : vatNumber;

      // ✅ Create Order with GST/VAT
      const { data: orderData } = await axios.post(
        `${EXCHNAGE_UR_PAYMENTSL}/create-orders`,
        {
          awardId: 1,
          amount: Math.round(totalPrice * 100),
          currency: currencyType || "INR",
          gstNumber: currencyType === "INR" ? gstNumber : "",    // ✅ GST for INR
          vatNumber: currencyType !== "INR" ? vatNumber : "",    // ✅ VAT for USD/AED
          companyName: companyName,
          eventid: eventId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      // ✅ Create Payment Intent with GST/VAT
      const { data } = await axios.post(
        `${EXCHNAGE_UR_PAYMENTSL}/stripe-payment-intent`,
        {
          amount: Math.round(totalPrice * 100),
          orderId: orderData.orderId,
          eventid: eventId,
          currency: currencyType || "INR",
          gstNumber: currencyType === "INR" ? gstNumber : "",    // ✅ GST for INR
          vatNumber: currencyType !== "INR" ? vatNumber : "",    // ✅ VAT for USD/AED
          companyName: companyName,                              // ✅ Company Name
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingDetails.name,
            address: { line1: billingDetails.addressLine1, country: 'IN' },
          },
        },
      });

      if (result.error) throw result.error;

      if (result.paymentIntent.status === 'succeeded') {
        const timestamp = result.paymentIntent.created * 1000; 
        navigate('/payment-status', {
          state: {
            userName: billingDetails.name,
            orderId: orderData.orderId,
            status: result.paymentIntent.status,
            timestamp: Date.now(),
            amount: finalAmount,
            currencySymbol: currencySymbol,
          },
        });
      }
    } catch (error) {
      console.error("Stripe Payment Error:", error);
      toast.error(error.message || 'Payment failed');
      setShowCardForm(false);
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  return (
    <div className="stripe-payment">
      {showCardForm && (
        <div className="card-form-section">
          <div className="billing-details-form">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                value={billingDetails.name}
                onChange={handleInputChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={billingDetails.addressLine1}
                onChange={handleInputChange}
                placeholder="Street address"
                required
              />
            </div>
          </div>

          <div className="card-element-container">
            <CardElement 
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
          
          <div className="security-message">
            🔒 Secure payment processing
          </div>
        </div>
      )}

      <RedBackgroundButton 
        onClick={handleStripePay}
        disabled={isProcessing}
        className={`payment-button ${showCardForm ? 'confirm-mode' : ''}`}
      >
        {isProcessing ? (
          <span className="processing-spinner"></span>
        ) : showCardForm ? (
          `Confirm Payment - ${currencySymbol}${finalAmount}`
        ) : (
          `Pay - ${currencySymbol}${finalAmount}`
        )}
      </RedBackgroundButton>
    </div>
  );
};

export default StripePayment;